import { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { Card, Table, Input, Loader, Alert } from '@dreamtree-org/twreact-ui';
import { searchableKeys, filterRows, clampPage } from './dataTableFilter';

/**
 * The app's one list-table primitive: a thin composition adapter over twreact-ui <Table>
 * (never a re-implementation — see .claude/skills/github-workflow) that supplies this
 * app's defaults and a `toolbar` slot for page-specific filters.
 *
 * It exists because <Table>'s search/filter/action defaults are unusable as shipped. All
 * four are verified in dist/index.js and filed upstream against twreact-ui:
 *   1. `globalSearch` is inert unless `filterable` is ALSO true — the filteredData memo
 *      early-returns on !filterable. So the search box renders and does nothing.
 *   2. `filterable` is not the fix: it adds a second <tr> of per-column inputs that never
 *      filter (the memo only ever reads filters.global), and a non-empty filter value
 *      then makes includes('') true for every row.
 *   3. `withAction` defaults TRUE with DEFAULT_ACTIONS whose handlers are console.log —
 *      a bare <Table> sprouts a working-looking Edit/Delete menu that does nothing.
 *   4. DEFAULT_THEME.stripedColors are Tailwind classes ("bg-[#ffffff]") but are applied
 *      as style.backgroundColor, which is invalid CSS — striping silently no-ops.
 * So: search is owned here (live, and restricted to named columns so a numeric query
 * can't match hidden ids), filterable/globalSearch stay off, actions are opt-in, and
 * striping is off in favour of the bordered rows the hand-rolled tables already used.
 */

/** thead/details/menu colours as classes; `rowHover` is prefixed with `hover:` by the
 *  library, so it must NOT carry the variant itself. accentColor matches the thead the
 *  hand-rolled tables used, so migrated pages look unchanged in light AND dark mode. */
const THEME = {
  accentColor: 'bg-[var(--app-border)]/30',
  rowHover: 'bg-[var(--app-border)]/20',
  menuBg: 'bg-[var(--app-surface)]',
  menuItemHover: 'hover:bg-[var(--app-border)]/20',
};

function DataTableToolbar({ toolbar, search, query, setQuery }) {
  if (!toolbar && !search) return null;
  const placeholder = (typeof search === 'object' && search.placeholder) || 'Search…';
  return (
    <div className="flex flex-wrap items-center gap-3">
      {toolbar}
      {search && (
        <div className="ml-auto w-full sm:w-64">
          <Input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder}
            icon={<Search className="h-4 w-4" />}
            iconPosition="left"
            clearable
            onClear={() => setQuery('')}
            aria-label="Search this table"
          />
        </div>
      )}
    </div>
  );
}

/** Split from DataTable so neither function trips the complexity/line-count lint gates. */
function DataTableGrid({ rows, columns, actions, onReload, serverSide, ...rest }) {
  // <Table> keeps its own `limit` state, so in serverSide mode its limit selector would
  // silently desync from the page size the caller actually asked the server for.
  const limitProps = serverSide ? { showLimitSelector: false } : {};
  return (
    <Card className="p-0">
      <Table
        {...rest}
        {...limitProps}
        data={rows}
        columns={columns}
        serverSide={serverSide}
        theme={THEME}
        stripedRows={false}
        showSerial={false}
        filterable={false}
        globalSearch={false}
        withAction={!!actions}
        actions={actions || []}
        showReloadButton={!!onReload}
        onReload={onReload}
      />
    </Card>
  );
}

/**
 * Client-side paging driven from here so it can follow the row count (see clampPage).
 * A caller that pages itself (serverSide, or its own pageNumber) keeps full control —
 * such a caller gets no props at all.
 */
function useClientPaging({ serverSide, callerPage, total, query, pageSize, onPageChange, onLimitChange }) {
  const enabled = !serverSide && callerPage === undefined;
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(pageSize);
  useEffect(() => { setLimit(pageSize); }, [pageSize]);
  useEffect(() => { if (enabled) setPage(1); }, [query, enabled]);
  const safePage = clampPage(page, total, limit);
  useEffect(() => { if (enabled && safePage !== page) setPage(safePage); }, [enabled, safePage, page]);
  if (!enabled) return {};
  return {
    pageNumber: safePage,
    onPageChange: (p) => { setPage(p); if (onPageChange) onPageChange(p); },
    onLimitChange: (l) => {
      setLimit(Number(l) || pageSize);
      setPage(1);
      if (onLimitChange) onLimitChange(l);
    },
  };
}

export default function DataTable({
  data = [],
  columns = [],
  loading = false,
  error = '',
  emptyText = 'Nothing here yet.',
  search = false,
  toolbar = null,
  serverSide = false,
  pagination = true,
  pageSize = 25,
  // Called with the post-search rows so a parent can scope bulk actions (or a select-all
  // checkbox) to exactly what the user sees, across pages. The search box lives in here,
  // so without this the parent only knows its own pre-search filtering.
  onFilteredRows = null,
  ...rest
}) {
  const [query, setQuery] = useState('');
  const keys = useMemo(
    () => (typeof search === 'object' && search.keys) || searchableKeys(columns),
    [search, columns],
  );
  // In serverSide mode the toolbar owns the query (a `where` on the server), so filtering
  // here as well would hide rows the server already narrowed.
  const rows = useMemo(
    () => (serverSide ? data : filterRows(data, query, keys)),
    [serverSide, data, query, keys],
  );
  useEffect(() => {
    if (onFilteredRows) onFilteredRows(rows);
  }, [rows, onFilteredRows]);

  const pagingProps = useClientPaging({
    serverSide,
    callerPage: rest.pageNumber,
    total: rows.length,
    query,
    pageSize,
    onPageChange: rest.onPageChange,
    onLimitChange: rest.onLimitChange,
  });

  const toolbarEl = (
    <DataTableToolbar toolbar={toolbar} search={search} query={query} setQuery={setQuery} />
  );

  if (loading) return <div className="space-y-4">{toolbarEl}<Loader text="Loading…" /></div>;
  if (error) return <div className="space-y-4">{toolbarEl}<Alert variant="error" title="Could not load">{error}</Alert></div>;

  return (
    <div className="space-y-4">
      {toolbarEl}
      {/* An all-empty list gets this app's own copy; a search that matched nothing keeps
          the grid mounted (<Table> says "No results found.") so the box stays clearable. */}
      {data.length === 0
        ? <Card className="p-6 text-center text-sm text-secondary-500">{emptyText}</Card>
        : (
          <DataTableGrid
            rows={rows}
            columns={columns}
            serverSide={serverSide}
            pagination={pagination}
            pageSize={pageSize}
            {...rest}
            {...pagingProps}
          />
        )}
    </div>
  );
}
